/**
 * Platform integrations — OUR backend credentials, not a tenant's.
 *
 * Deliberately a separate page from the per-org integrations on the tenant side.
 * They are different things wearing the same word: a tenant's Datto key belongs
 * to that tenant and they configure it themselves; the Claude API key belongs to
 * the platform, is billed across every tenant, and no org owner should be able
 * to read or replace it. Same word, opposite blast radius.
 *
 * The value is never sent back to the browser — only whether each field is set.
 * Saving a blank field leaves the existing secret alone, so this form can be
 * rendered without ever round-tripping a live credential through a page.
 *
 * TEST IS NOT OPTIONAL POLISH. "The key is saved" and "the key works" are
 * different claims, and the gap between them is where an expired key or an empty
 * credit balance hides. The test makes a real round trip and shows the vendor's
 * own message, because "invalid key" and "credit balance too low" need
 * completely different actions from you.
 */

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { HostsView } from './hosts';
import { AiUsageView } from './ai-usage';
import {
  getPlatformIntegrations, setPlatformIntegration, testPlatformIntegration,
  type PlatformIntegration,
} from './api';

const STATE_LABEL: Record<string, string> = {
  set: 'Saved',
  unset: 'Not set',
  unreadable: 'Saved, but this console cannot read it',
};


/**
 * WHICH HOSTNAMES MAY SIGN IN.
 *
 * Firebase refuses authentication from any origin that is not on this list, and
 * the refusal surfaces as auth/unauthorized-domain - which a login form will
 * very reasonably render as "that did not work", sending somebody to check a
 * password that was never the problem. Moving either console to a custom domain
 * is exactly when this bites, and it bites at the moment you can no longer sign
 * in to go and look.
 *
 * So the list is shown here, in the product, with THIS host called out against
 * it. There is no remove button on purpose: removing a domain is rare,
 * dangerous, and undoable only from a domain you may have just removed.
 */
function AuthorisedDomains() {
  const [domains, setDomains] = useState<string[] | null>(null);
  const [thisHostAllowed, setThisHostAllowed] = useState<boolean | null>(null);
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const host = window.location.hostname;

  const load = useCallback(async () => {
    try {
      const r = await httpsCallable(functions, 'adminGetAuthDomains')({ host });
      const d = r.data as { domains: string[]; thisHostAllowed: boolean | null };
      setDomains(d.domains ?? []);
      setThisHostAllowed(d.thisHostAllowed);
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'Could not read the list.' });
      setDomains([]);
    }
  }, [host]);

  useEffect(() => { void load(); }, [load]);

  async function add() {
    setBusy(true); setMsg(null);
    try {
      const r = await httpsCallable(functions, 'adminAddAuthDomain')({ domain: next.trim() });
      const d = r.data as { added: boolean; domains: string[] };
      setDomains(d.domains);
      setMsg({
        ok: true,
        text: d.added ? `${next.trim()} can now sign in.` : `${next.trim()} was already on the list.`,
      });
      setNext('');
      await load();
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : 'That did not save.' });
    } finally { setBusy(false); }
  }

  return (
    <section className="panel">
      <div className="panel-title">Authorised sign-in domains</div>
      <p className="panel-note">
        Firebase will only authenticate from these hostnames. A console reached at any other
        address can load, but nobody can sign in to it.
      </p>

      {thisHostAllowed === false && (
        <div className="banner banner-error">
          You are on <strong>{host}</strong>, which is <strong>not</strong> on this list. Add it below.
        </div>
      )}
      {thisHostAllowed === true && (
        <p className="panel-note">
          You are on <strong>{host}</strong>, which is on the list.
        </p>
      )}

      {msg && <div className={'banner ' + (msg.ok ? 'banner-ok' : 'banner-error')}>{msg.text}</div>}

      {domains === null ? (
        <p className="empty">Loading.</p>
      ) : (
        <ul className="domain-list">
          {domains.map((d) => (
            <li key={d} className={d === host ? 'domain-here' : undefined}>{d}</li>
          ))}
        </ul>
      )}

      <div className="row-form">
        <label className="field" style={{ flex: 2 }}>
          <span className="field-label">Add a hostname</span>
          <input
            className="field-input" placeholder="bdadmin.example.com"
            value={next} onChange={(e) => setNext(e.target.value)}
          />
        </label>
        <button className="btn btn-primary" disabled={busy || !next.trim()} onClick={() => void add()}>
          {busy ? 'Adding.' : 'Add'}
        </button>
      </div>
    </section>
  );
}
export function PlatformIntegrationsView() {
  /**
   * The org list, only so a hostname can be pointed at one by NAME.
   * adminOverview is the console's existing answer to "what tenants exist";
   * reaching for a second callable here would be a second version of that
   * question, and the two would disagree the first time one was cached.
   */
  const [orgOptions, setOrgOptions] = useState<{ orgId: string; name: string }[]>([]);
  useEffect(() => {
    void (async () => {
      try {
        const r = await httpsCallable(functions, 'adminOverview')({});
        const orgs = (r.data as { orgs?: { orgId: string; name: string }[] }).orgs ?? [];
        setOrgOptions(orgs.map((o) => ({ orgId: o.orgId, name: o.name })));
      } catch {
        // The domain form degrades to unusable-but-visible rather than the page
        // failing; the message on Map will say what went wrong.
        setOrgOptions([]);
      }
    })();
  }, []);
  const [rows, setRows] = useState<PlatformIntegration[] | null>(null);
  const [draft, setDraft] = useState<Record<string, Record<string, string>>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<Record<string, { ok: boolean; text: string }>>({});
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const r = await getPlatformIntegrations({});
      setRows(r.integrations);
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : 'Could not load integrations.');
    }
  }

  useEffect(() => { void load(); }, []);

  async function save(provider: string) {
    setBusy(provider);
    setError(null);
    try {
      const r = await setPlatformIntegration({ provider, values: draft[provider] ?? {}, enabled: true });
      setDraft((d) => ({ ...d, [provider]: {} }));
      // `warning` means the key stored but the service cannot read it yet —
      // which looks exactly like success until something tries to use it.
      setMsg((m) => ({
        ...m,
        [provider]: r.warning ? { ok: false, text: r.warning } : { ok: true, text: 'Saved.' },
      }));
      await load();
    } catch (err) {
      setMsg((m) => ({
        ...m,
        [provider]: { ok: false, text: err instanceof Error ? err.message : 'Save failed.' },
      }));
    } finally { setBusy(null); }
  }

  async function test(provider: string) {
    setBusy(provider);
    try {
      const r = await testPlatformIntegration({ provider });
      setMsg((m) => ({ ...m, [provider]: { ok: r.ok, text: r.message } }));
    } catch (err) {
      setMsg((m) => ({
        ...m,
        [provider]: { ok: false, text: err instanceof Error ? err.message : 'Test failed.' },
      }));
    } finally { setBusy(null); }
  }

  if (rows === null) return <div className="empty">Loading…</div>;

  return (
    <div>
      {error && <div className="banner banner-error">{error}</div>}

      {/* FIRST ON THE PAGE, and deliberately above the credentials. If this list
          is wrong nobody can sign in to reach anything below it, so it is the
          one thing on this screen that can lock you out of the screen. */}
      <AuthorisedDomains />

      {/* Directly beneath, because mapping a domain without authorising it
          produces a beautifully branded page nobody can sign in to. */}
      <HostsView orgs={orgOptions} />

      <p className="panel-note" style={{ marginTop: 0 }}>
        Credentials for the product itself, shared across every tenant. Tenants configure
        their own vendor credentials in their own dashboard; nothing here is visible to them.
      </p>

      {/* Under the credentials on purpose: the Claude key and what it costs
          are the same subject, and a key with no visible spend beside it is
          how an invoice becomes a surprise. */}
      <AiUsageView />

      {rows.map((r) => {
        const m = msg[r.provider];
        return (
          <div className="card" key={r.provider} style={{ marginBottom: 14 }}>
            <div className="row-between">
              <div>
                <div className="card-title">{r.label}</div>
                <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>{r.hint}</div>
              </div>
              <span className={'tag ' + (r.fields.every((f) => f.state === 'set') ? 'tag-ok' : 'tag-bad')}>
                {r.fields.every((f) => f.state === 'set') ? 'Configured' : 'Not configured'}
              </span>
            </div>

            {r.fields.map((f) => {
              // A sender address is configuration, not a credential. Masking it
              // and refusing to read it back would mean nobody could answer
              // "what address does our mail come from?" without sending some.
              const isSecret = f.secret !== false;
              const draftValue = draft[r.provider]?.[f.key];
              return (
                <div className="field" key={f.key} style={{ marginTop: 12 }}>
                  <span className="field-label">
                    {f.label} <span className="muted">· {STATE_LABEL[f.state] ?? f.state}</span>
                  </span>
                  <input
                    className="field-input"
                    type={isSecret ? 'password' : 'text'}
                    autoComplete="off"
                    placeholder={
                      isSecret
                        ? (f.state === 'set' ? 'Leave blank to keep the current key' : 'Paste the key')
                        : (f.placeholder ?? '')
                    }
                    value={draftValue ?? (isSecret ? '' : (f.value ?? ''))}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        [r.provider]: { ...(d[r.provider] ?? {}), [f.key]: e.target.value },
                      }))
                    }
                  />
                </div>
              );
            })}

            {m && (
              <div className={'banner ' + (m.ok ? 'banner-ok' : 'banner-error')} style={{ marginTop: 12 }}>
                {m.text}
              </div>
            )}

            <div className="row-actions" style={{ marginTop: 12 }}>
              <button
                className="btn btn-primary"
                disabled={busy === r.provider}
                onClick={() => void save(r.provider)}
              >
                {busy === r.provider ? 'Working…' : 'Save'}
              </button>
              <button
                className="btn"
                disabled={busy === r.provider}
                onClick={() => void test(r.provider)}
                title="Sends a real message to your own address"
              >
                Test
              </button>
              {r.updatedAt && (
                <span className="muted" style={{ fontSize: 12 }}>
                  Last changed {new Date(r.updatedAt).toLocaleString()}
                  {r.updatedBy ? ` by ${r.updatedBy}` : ''}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
